import type { ComponentType } from 'react';

/**
 * Lazy route targets, grouped to mirror the URL tree. Each keeps its own `import()`, so routing
 * through the page's public API would merge every screen into one chunk.
 */
const screen = (load: () => Promise<{ default: ComponentType }>) => () => load().then(m => ({ Component: m.default }));

export const patientDetailsRoutes = {
  /** Access guard, nav and `<Outlet />`. */
  shell: screen(() => import('./ui')),

  patientHistory: screen(() => import('./ui/patientRequestHistory')),
  medicationHistory: screen(() => import('./ui/medicationHistory')),
  conversations: screen(() => import('./ui/Messaging/PatientConversations')),
  followUps: screen(() => import('./ui/FollowUps/AllFollowUps')),
  notes: screen(() => import('./ui/RemotePatientMonitoring/Notes')),
  accountDocuments: screen(() => import('./ui/RemotePatientMonitoring/AccountDocuments')),
  tcmTasks: () => import('./ui/TransitionalCareManagement').then(m => ({ Component: m.TCMTasks })),

  medications: {
    list: screen(() => import('./ui/medications-route')),
    details: screen(() => import('./ui/MedicationDetails')),
    encounterDetails: screen(() => import('./ui/Encounters/EncounterDetails')),
    medRecHistory: screen(() => import('./ui/medRecHistory'))
  },

  tasks: {
    list: screen(() => import('./ui/Tasks/tasksList')),
    detail: screen(() => import('./ui/tasks-route'))
  },

  triage: {
    section: screen(() => import('./ui/Triage/index')),
    list: screen(() => import('./ui/Triage/triageMedicationList')),
    board: screen(() => import('./ui/Triage/triageMedicationBoard')),
    verify: screen(() => import('./ui/Triage/triageMedicationVerify'))
  },

  rpm: {
    readings: screen(() => import('./ui/RemotePatientMonitoring/DataReadings')),
    workflow: screen(() => import('./ui/RemotePatientMonitoring/RpmWorkflow')),
    clinicalSummary: screen(() => import('./ui/RemotePatientMonitoring/ClinicalSummary')),
    medReconciliation: screen(() => import('./ui/RemotePatientMonitoring/MedReconciliation')),
    configuration: screen(() => import('./ui/RemotePatientMonitoring/Configuration')),
    consentManagement: screen(() => import('./ui/RemotePatientMonitoring/ConsentManagement')),
    carePlan: screen(() => import('./ui/RemotePatientMonitoring/CarePlan')),
    devices: screen(() => import('./ui/RemotePatientMonitoring/RpmDevices')),
    medConsults: screen(() => import('./ui/RemotePatientMonitoring/MedConsults')),
    activityLogs: screen(() => import('./ui/RemotePatientMonitoring/ActivityLogs')),
    reports: screen(() => import('./ui/RemotePatientMonitoring/Reports')),
    alerts: screen(() => import('./ui/rpm-alerts'))
  },

  rpmFollowup: {
    section: screen(() => import('./ui/RemotePatientMonitoring/RpmFollowupConsultSection')),
    consentManagement: screen(() => import('./ui/RemotePatientMonitoring/RpmFollowupConsentManagement')),
    clinicalSummary: screen(() => import('./ui/RemotePatientMonitoring/RpmFollowupClinicalSummary')),
    carePlan: screen(() => import('./ui/RemotePatientMonitoring/RpmFollowupCarePlan'))
  },

  clinicalCare: {
    section: screen(() => import('./ui/CareManagement/ClinicalCareEncounterSection')),
    clinicalSummary: screen(() => import('./ui/CareManagement/CCEncounterClinicalSummary')),
    consentManagement: screen(() => import('./ui/CareManagement/CCMConsentManagement')),
    carePlan: screen(() => import('./ui/CareManagement/CCEncounterCarePlan'))
  },

  imports: {
    dischargeSummary: screen(() => import('./ui/DischargeReports/Summary')),
    adtEventDetails: screen(() => import('./ui/imports/AdtEventDetails')),
    pioneer: screen(() => import('./ui/imports/PioneerImports')),
    pioneerObjects: screen(() => import('./ui/imports/PioneerObjects')),
    athena: screen(() => import('./ui/imports/AthenaObjects')),
    uploadFile: screen(() => import('./ui/uploadFile')),
    directImport: screen(() => import('./ui/directImport')),
    preview: screen(() => import('./ui/importPreview'))
  },

  healthApp: {
    medications: screen(() => import('./ui/HealthApps/medications')),
    immunizations: screen(() => import('./ui/HealthApps/immunizations')),
    energy: () => import('./ui/HealthApps/energy').then(m => ({ Component: m.Energy })),
    allergies: () => import('./ui/HealthApps/allergies').then(m => ({ Component: m.Allergies })),
    vitals: () => import('./ui/HealthApps/vitals').then(m => ({ Component: m.Vitals }))
  },

  quickUpload: {
    history: screen(() => import('./ui/quickUploadHistory')),
    items: screen(() => import('./ui/quickUploadItems'))
  }
};
